import { useEffect } from 'react';

interface HeyGenAgentProps {
  heygenAgentUrl?: string | null;
}

// Déclarations TypeScript pour les propriétés personnalisées
declare global {
  interface Window {
    heygenPositionTimeout?: number;
    heygenResizeTimeout?: number;
  }
}

interface ExtendedHTMLElement extends HTMLElement {
  _cleanup?: () => void;
}

function buildHeygenUrl(raw: string): string {
  let url = raw;
  if (!url) return '';
  if (!url.startsWith('http')) {
    url = `https://labs.heygen.com/guest/streaming-embed?share=${raw}`;
  }
  url = url.replace('/interactive-avatar/share', '/guest/streaming-embed');
  if (!url.includes('&inIFrame=1')) {
    url += '&inIFrame=1';
  }
  return url;
}

export default function HeyGenAgent({ heygenAgentUrl }: HeyGenAgentProps) {
  useEffect(() => {
    if (!heygenAgentUrl) return;

    const heygenUrl = buildHeygenUrl(heygenAgentUrl);

    const existingScript = document.querySelector('script[data-heygen="true"]');
    if (existingScript) {
      return;
    }

    const script = document.createElement('script');
    script.setAttribute('data-heygen', 'true');
    const CHAT_LEFT = '88px';   // distance depuis le bord gauche
    const CHAT_BOTTOM = '32px';  // distance depuis le bas
    script.innerHTML = `
      !function(window){
        const host="https://labs.heygen.com";
        const url="${heygenUrl}";
        const clientWidth=document.body.clientWidth;
        const wrapDiv=document.createElement("div");
        wrapDiv.id="heygen-streaming-embed";
        const container=document.createElement("div");
        container.id="heygen-streaming-container";
        const stylesheet=document.createElement("style");

        // Créer le backdrop pour la modale
        const backdrop=document.createElement("div");
        backdrop.id="heygen-modal-backdrop";
        backdrop.innerHTML="";

        // Créer le bouton callout avec bulle
        const calloutButton=document.createElement("div");
        calloutButton.id="heygen-callout-button";
        calloutButton.innerHTML='<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 12H16M8 8H16M8 16H12M21 12C21 16.9706 16.9706 21 12 21C10.2682 21 8.64776 20.4933 7.29289 19.625L3 21L4.375 16.7071C3.50671 15.3522 3 13.7318 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        
        // Créer la bulle callout
        const calloutBubble=document.createElement("div");
        calloutBubble.id="heygen-callout-bubble";
        calloutBubble.innerHTML="Besoin d'aide ? Posez-moi vos questions !";

        // Créer le bouton de fin de chat (en haut à gauche de la modale)
        const endChatButton=document.createElement("div");
        endChatButton.id="heygen-endchat-button";
        endChatButton.innerHTML='Terminer le chat';
        endChatButton.style.display='none';

        // Fonction pour détecter si la sidebar est ouverte
        function isSidebarOpen() {
          const overlay = document.querySelector('.fixed.inset-0.bg-black.bg-opacity-25');
          if (overlay) return true;
          const sidebar = document.querySelector('.fixed.top-0.left-0.h-full.w-80');
          if (sidebar) {
            const transform = window.getComputedStyle(sidebar).transform;
            return !transform.includes('translateX(-100%)') && transform !== 'none';
          }
          return false;
        }
        
        // Fonction pour calculer la position en fonction de l'état de la sidebar
        function getCalloutPosition() {
          const isOpen = isSidebarOpen();
          const isLargeScreen = window.innerWidth >= 1024; // lg breakpoint
          const isMediumScreen = window.innerWidth >= 768;  // md breakpoint
          
          let rightPosition = '24px';
          if (isOpen && isLargeScreen) {
            rightPosition = '344px'; // 24px + 320px
          } else if (isOpen && isMediumScreen) {
            rightPosition = '344px';
          } else {
            rightPosition = '24px';
          }
          return rightPosition;
        }

        // Fonction pour détecter si une modale pleine page est ouverte
        function isFullPageModalOpen() {
          const modals = document.querySelectorAll('.fixed.inset-0, [role="dialog"].fixed');
          return modals.length > 0;
        }

        stylesheet.innerHTML=\`
  /* Bouton callout discret en bas à gauche */
  #heygen-callout-button {
    z-index: 9999;
    position: fixed;
    left: ${CHAT_LEFT};     /* <- fixe */
    bottom: ${CHAT_BOTTOM};   /* <- fixe */
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: 1;
    visibility: visible;
    transform: scale(1);
  }
  
  #heygen-callout-button:hover {
    transform: scale(1.05) translateY(-2px);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.6);
  }

  #heygen-callout-button:active {
    transform: scale(0.95);
  }

  /* Bouton Terminer (en haut à gauche de la modale) */
  #heygen-endchat-button {
    z-index: 10002; /* au-dessus du backdrop (10000) et de l'embed (10001) */
    position: absolute;
    top: 12px;
    left: 12px !important;     /* force la position à gauche (neutralise le inline style) */
    right: auto !important;    /* neutralise d'éventuels updates right/bottom */
    bottom: auto !important;   /* neutralise d'éventuels updates right/bottom */
    padding: 8px 14px;
    height: auto;
    border-radius: 9999px;
    background: #e11d48;
    color: white;
    box-shadow: 0 6px 20px rgba(225, 29, 72, 0.35);
    cursor: pointer;
    display: none;           /* affiché seulement quand la modale est ouverte */
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
    user-select: none;
    -webkit-user-select: none;
    white-space: nowrap;
  }

  #heygen-endchat-button::before {
    content: '✕';
    font-weight: 700;
  }

  #heygen-endchat-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(225, 29, 72, 0.5);
  }

  /* Bulle callout avec triangle */
  #heygen-callout-bubble {
    position: absolute;
    bottom: 70px;
    left: 0;
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transform: translateY(8px);
    transition: all 0.3s ease;
    pointer-events: none;
    z-index: 10000;
    max-width: 200px;
    white-space: normal;
    line-height: 1.4;
  }
  
  /* Triangle de la bulle callout */
  #heygen-callout-bubble::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 16px;
    border: 6px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
  
  #heygen-callout-button:hover #heygen-callout-bubble {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  /* Conteneur de l'agent HeyGen */
  #heygen-streaming-embed {
    z-index: 10001;
    position: fixed;
    top: 50%;
    left: 50%;
    width: 480px;
    height: 640px;
    max-width: 90vw;
    max-height: 80vh;
    border-radius: 16px;
    box-shadow: 0 20px 64px rgba(0, 0, 0, 0.3);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    opacity: 0;
    visibility: hidden;
    transform: translate(-50%, -50%) scale(0.8);
    pointer-events: none;
    background: white;
  }
  
  #heygen-streaming-embed.show {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, -50%) scale(1);
    pointer-events: auto;
  }

  /* Backdrop de la modale */
  #heygen-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 10000;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
    pointer-events: none;
  }

  #heygen-modal-backdrop.show {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  /* Conteneur de l'iframe */
  #heygen-streaming-container {
    width: 100%;
    height: 100%;
    position: relative;
  }

  #heygen-streaming-container iframe {
    width: 100%;
    height: 100%;
    border: 0;
    border-radius: inherit;
  }

  /* Masquer les boutons quand une modale externe est ouverte */
  #heygen-callout-button.hidden-modal,
  #heygen-endchat-button.hidden-modal {
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease;
    pointer-events: none;
    white-space: nowrap;
    z-index: 10000;
  }
  
  #heygen-chat-badge::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.8);
  }
  
  #heygen-streaming-embed:hover #heygen-chat-badge {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(-4px);
  }
  
  #heygen-streaming-embed.expand #heygen-chat-badge {
    display: none;
  }
  
  /* Logo audio au centre */
  #heygen-audio-logo {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 32px;
    z-index: 1;
    opacity: 1;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    #heygen-callout-button {
      left: 16px !important;
      bottom: 16px;
      width: 52px;
      height: 52px;
    }
    
    #heygen-callout-bubble {
      left: -8px;
      max-width: calc(100vw - 80px);
    }

    /* En haut à gauche aussi sur mobile */
    #heygen-endchat-button {
      top: 10px;
      left: 10px !important;
      right: auto !important;
      height: auto;
    }
    
    #heygen-streaming-embed {
      width: 95vw;
      height: 70vh;
      border-radius: 12px;
    }
  }

  @media (max-width: 480px) {
    #heygen-callout-button {
      width: 48px;
      height: 48px;
    }

    #heygen-callout-button svg {
      width: 20px;
      height: 20px;
    }

    #heygen-endchat-button {
      font-size: 13px;
      padding: 8px 12px;
    }
  }
  \`;

        const iframe=document.createElement("iframe");
        iframe.allowFullscreen=false;
        iframe.title="Assistant IA - Streaming Embed";
        iframe.role="dialog";
        iframe.allow="microphone";
        iframe.src=url;
        let visible=false, initial=false;
        
        // Variables pour stocker les timeouts et l'observer
        let positionTimeout;
        let resizeTimeout;
        let modalTimeout;
        let observer;
        
        // Fonction pour mettre à jour la position du bouton callout
        function updatePosition() {
          // Position toujours fixe (pour le bouton flottant)
          calloutButton.style.left = ${JSON.stringify(CHAT_LEFT)};
          calloutButton.style.bottom = ${JSON.stringify(CHAT_BOTTOM)};
          // Le bouton "Terminer" est dans la modale (position absolue en haut à gauche).
          // Ces 2 lignes seront neutralisées par !important dans le CSS.
          endChatButton.style.left = 'calc(${CHAT_LEFT} + 72px)';
          endChatButton.style.bottom = ${JSON.stringify(CHAT_BOTTOM)};
          
          // Vérifier si une modale externe est ouverte et masquer les boutons si nécessaire
          const modalOpen = isFullPageModalOpen();
          if (modalOpen && !visible) {
            calloutButton.classList.add('hidden-modal');
            endChatButton.classList.add('hidden-modal');
          } else if (!modalOpen) {
            calloutButton.classList.remove('hidden-modal');
            endChatButton.classList.remove('hidden-modal');
          }
        }

        // Fonction pour ouvrir la modale HeyGen
        function openHeygenModal() {
          visible = true;
          backdrop.classList.add('show');
          wrapDiv.classList.add('show');
          endChatButton.style.display = 'flex';
          document.body.style.overflow = 'hidden';
        }

        // Fonction pour fermer la modale HeyGen
        function closeHeygenModal() {
          visible = false;
          backdrop.classList.remove('show');
          wrapDiv.classList.remove('show');
          document.body.style.overflow = '';
        }

        // Gestionnaire de clic sur le bouton callout
        calloutButton.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          if (!visible) {
            openHeygenModal();
            // Envoyer un message à l'iframe pour déclencher l'affichage
            if (iframe.contentWindow) {
              iframe.contentWindow.postMessage({
                type: 'streaming-embed',
                action: 'show'
              }, host);
            }
          }
        });

        // Gestionnaire de clic sur le bouton "Terminer" (en haut à gauche)
        endChatButton.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          if (iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'streaming-embed',
              action: 'hide'
            }, host);
          }
          iframe.src = url;
          endChatButton.style.display = 'none';
          closeHeygenModal();
        });

        // Gestionnaire de clic sur le backdrop pour fermer
        backdrop.addEventListener('click', function(e) {
          if (e.target === backdrop) {
            closeHeygenModal();
            if (iframe.contentWindow) {
              iframe.contentWindow.postMessage({
                type: 'streaming-embed',
                action: 'hide'
              }, host);
            }
          }
        });

        // Gestionnaire pour la touche Escape
        document.addEventListener('keydown', function(e) {
          if (e.key === 'Escape' && visible) {
            closeHeygenModal();
            if (iframe.contentWindow) {
              iframe.contentWindow.postMessage({
                type: 'streaming-embed',
                action: 'hide'
              }, host);
            }
          }
        });

        // Observer les changements dans le DOM pour détecter l'ouverture/fermeture de la sidebar
        observer = new MutationObserver(function(mutations) {
          mutations.forEach(function(mutation) {
            if (mutation.type === 'attributes' || mutation.type === 'childList') {
              if (positionTimeout) clearTimeout(positionTimeout);
              positionTimeout = setTimeout(updatePosition, 100);
            }
          });
        });
        
        // Observer le body pour les changements de classe et les ajouts/suppressions d'éléments
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style']
        });
        
        // Écouter les redimensionnements de fenêtre
        function handleResize() {
          if (resizeTimeout) clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(updatePosition, 100);
        }
        
        window.addEventListener('resize', handleResize);

        window.addEventListener("message",(e=>{
          if(e.origin===host&&e.data&&e.data.type&&"streaming-embed"===e.data.type){
            if("init"===e.data.action){
              initial=true;
              setTimeout(updatePosition, 100);
            }else if("show"===e.data.action){
              // Affichage géré par openHeygenModal
            }else if("hide"===e.data.action){
              closeHeygenModal();
              endChatButton.style.display = 'none';
              iframe.src = url;
            }
          }
        }));

        container.appendChild(iframe);
        calloutButton.appendChild(calloutBubble);
        wrapDiv.appendChild(stylesheet);
        wrapDiv.appendChild(container);
        document.body.appendChild(backdrop);
        /* Le bouton Terminer vit DANS la modale (wrapDiv) */
        wrapDiv.appendChild(endChatButton);
        document.body.appendChild(calloutButton);
        document.body.appendChild(wrapDiv);
        
        // Stocker la fonction de nettoyage
        wrapDiv.cleanupFunction = function() {
          if (observer) observer.disconnect();
          if (positionTimeout) clearTimeout(positionTimeout);
          if (resizeTimeout) clearTimeout(resizeTimeout);
          if (modalTimeout) clearTimeout(modalTimeout);
          window.removeEventListener('resize', handleResize);
          document.body.style.overflow = '';
        };
      }(globalThis);
    `;

    document.body.appendChild(script);

    return () => {
      const embedDiv = document.getElementById('heygen-streaming-embed') as ExtendedHTMLElement;
      const calloutButton = document.getElementById('heygen-callout-button');
      const endChatButton = document.getElementById('heygen-endchat-button');
      const backdrop = document.getElementById('heygen-modal-backdrop');

      if (embedDiv && (embedDiv as any).cleanupFunction) {
        (embedDiv as any).cleanupFunction();
      }
      if (embedDiv && embedDiv._cleanup) {
        embedDiv._cleanup();
      }
      if (embedDiv) {
        embedDiv.remove();
      }
      if (calloutButton) {
        calloutButton.remove();
      }
      if (endChatButton) {
        endChatButton.remove();
      }
      if (backdrop) {
        backdrop.remove();
      }
      const heygenScript = document.querySelector('script[data-heygen="true"]');
      if (heygenScript) {
        heygenScript.remove();
      }
      if (window.heygenPositionTimeout) {
        clearTimeout(window.heygenPositionTimeout);
        delete window.heygenPositionTimeout;
      }
      if (window.heygenResizeTimeout) {
        clearTimeout(window.heygenResizeTimeout);
        delete window.heygenResizeTimeout;
      }
    };
  }, [heygenAgentUrl]);

  return null;
}
